'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { parseFiltersFromHash, buildHashFromFilters, resolveSuppliers, resolveDesigners, resolveTags, formatPriceFilter, formatSortLabel, hasActiveFilters } from '@/app/(shop)/category/[...slug]/_lib/filterHelpers';

/**
 * useCategoryFilters - Central hook for category filter state management
 *
 * Manages:
 * - URL hash state (filters stored in hash for SEO)
 * - Filter parsing and resolution
 * - Product fetching when filters change
 * - Filter operations (add, remove, clear, sort)
 *
 * @param {Object} options
 * @param {string|number} options.categoryId - Category ID for product fetches
 * @param {Object} options.category - Category data with suppliers, designers, tagGroups, etc.
 * @param {Array} options.initialProducts - SSR products (shown when no filters active)
 * @returns {Object} Filter state and operations
 */
export function useCategoryFilters({
  categoryId,
  category,
  initialProducts
}) {
  const pathname = usePathname();

  // =========================================================================
  // HASH STATE
  // =========================================================================

  const [hash, setHash] = useState('');

  // Track initial render with filters
  const initialHadFilters = useRef(false);
  const isInitialMount = useRef(true);

  // Read hash on mount and listen for changes
  useEffect(() => {
    const initialHash = window.location.hash;
    setHash(initialHash);

    // Check if we had filters on initial load
    const initialFilters = parseFiltersFromHash(initialHash);
    if (hasActiveFilters(initialFilters)) {
      initialHadFilters.current = true;
    }

    // Listen for hash changes (back/forward navigation)
    const handleHashChange = () => {
      setHash(window.location.hash);
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // =========================================================================
  // DERIVED: Parsed Filters
  // =========================================================================

  const filters = useMemo(() => parseFiltersFromHash(hash), [hash]);
  const filtersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  // =========================================================================
  // DERIVED: Resolved Filters (IDs to display names)
  // =========================================================================

  const resolvedFilters = useMemo(() => ({
    suppliers: resolveSuppliers(filters.supplier_ids, category.suppliers),
    designers: resolveDesigners(filters.designer_ids, category.designers),
    tags: resolveTags(filters.tag_ids, category.tagGroups),
    price: formatPriceFilter(filters.min_price, filters.max_price),
    sort: formatSortLabel(filters.sort_by)
  }), [filters, category.suppliers, category.designers, category.tagGroups]);

  // =========================================================================
  // PRODUCTS STATE
  // =========================================================================

  // Deterministic initial state - never read window.location.hash during
  // render. The server renders the full grid; a client initializer that
  // sees a #sort_by/filter hash and starts empty disagrees with that HTML,
  // which is a guaranteed hydration mismatch (React #418) and, on iOS
  // Safari back/forward-cache restores, a blank product grid. The hash is
  // read in the mount effect above; the fetch effect below re-sorts.
  const [products, setProducts] = useState(initialProducts);
  const [isLoading, setIsLoading] = useState(false);
  const totalProductCount = initialProducts.length;

  // =========================================================================
  // EFFECTS: Fetch Products on Filter Change
  // =========================================================================

  useEffect(() => {
    // Skip on initial mount before hash is read
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // But if we had initial filters, we still need to fetch
      if (!initialHadFilters.current) {
        return;
      }
    }
    const currentFilters = parseFiltersFromHash(hash);
    const hasFilters = hasActiveFilters(currentFilters);
    const fetchProducts = async () => {
      // If no filters, use initial products
      if (!hasFilters) {
        setProducts(initialProducts);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const productFilters = {
        category_id: categoryId,
        suppliers: currentFilters.supplier_ids.length > 0 ? currentFilters.supplier_ids : undefined,
        designers: currentFilters.designer_ids.length > 0 ? currentFilters.designer_ids : undefined,
        tags: currentFilters.tag_ids.length > 0 ? currentFilters.tag_ids : undefined,
        price_filter: currentFilters.min_price || currentFilters.max_price ? {
          min: currentFilters.min_price,
          max: currentFilters.max_price
        } : undefined,
        sort_by: currentFilters.sort_by || undefined
      };

      // Handle child products for vendor parent layout (type 7)
      if (category.layout_type == 7) {
        productFilters.child_products = true;
      }

      // A failed filter fetch must degrade to the unfiltered grid, not
      // an empty one - a flaky mobile connection during a bfcache
      // restore otherwise leaves the page permanently blank
      try {
        const response = await fetch('/api/products', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(productFilters)
        });
        const data = await response.json();
        if (data.success) {
          setProducts(data.products || []);
        } else {
          setProducts(initialProducts);
        }
      } catch (error) {
        setProducts(initialProducts);
      }
      setIsLoading(false);
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, categoryId, category.layout_type]);

  // =========================================================================
  // OPERATIONS
  // =========================================================================

  // Update hash in URL
  const updateFilters = useCallback(newFilters => {
    const hashString = buildHashFromFilters(newFilters);
    const newUrl = hashString ? `${pathname}#${hashString}` : pathname;
    window.history.replaceState(null, '', newUrl);
    setHash(hashString ? `#${hashString}` : '');
  }, [pathname]);

  // Remove a single filter
  const removeFilter = useCallback((filterType, id) => {
    const newFilters = {
      ...filters
    };
    switch (filterType) {
      case 'supplier':
        newFilters.supplier_ids = filters.supplier_ids.filter(sid => sid !== id);
        break;
      case 'designer':
        newFilters.designer_ids = filters.designer_ids.filter(did => did !== id);
        break;
      case 'tag':
        newFilters.tag_ids = filters.tag_ids.filter(tid => tid !== id);
        break;
      case 'price':
        newFilters.min_price = null;
        newFilters.max_price = null;
        break;
      case 'sort':
        newFilters.sort_by = null;
        break;
    }
    updateFilters(newFilters);
  }, [filters, updateFilters]);

  // Clear all filters
  const clearAll = useCallback(() => {
    window.history.replaceState(null, '', pathname);
    setHash('');
  }, [pathname]);

  // Set sort option
  const setSort = useCallback(sortKey => {
    const newFilters = {
      ...filters,
      sort_by: sortKey || null
    };
    updateFilters(newFilters);
  }, [filters, updateFilters]);

  // Add a filter (for use by CategoryFilters component)
  const addFilter = useCallback((filterType, id) => {
    const newFilters = {
      ...filters
    };
    switch (filterType) {
      case 'supplier':
        if (!filters.supplier_ids.includes(id)) {
          newFilters.supplier_ids = [...filters.supplier_ids, id];
        }
        break;
      case 'designer':
        if (!filters.designer_ids.includes(id)) {
          newFilters.designer_ids = [...filters.designer_ids, id];
        }
        break;
      case 'tag':
        if (!filters.tag_ids.includes(id)) {
          newFilters.tag_ids = [...filters.tag_ids, id];
        }
        break;
    }
    updateFilters(newFilters);
  }, [filters, updateFilters]);

  // Set price range
  const setPriceRange = useCallback((min, max) => {
    const newFilters = {
      ...filters,
      min_price: min || null,
      max_price: max || null
    };
    updateFilters(newFilters);
  }, [filters, updateFilters]);

  // =========================================================================
  // RETURN
  // =========================================================================

  return {
    // Products
    products,
    isLoading,
    totalProductCount,
    // Filter state
    filters,
    filtersActive,
    resolvedFilters,
    // Operations
    updateFilters,
    addFilter,
    removeFilter,
    clearAll,
    setSort,
    setPriceRange
  };
}
export default useCategoryFilters;